/** Portable map exports. No filesystem writes, network calls or key material. */
import { t } from '../i18n/index.js';

export const ARCHIVE_MANIFEST = 'orbital-manifest.json';
export const MAX_ARCHIVE_BYTES = 256 * 1024 * 1024;
const MAX_MAP_BYTES = 50 * 1024 * 1024;
const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
const referencePattern = () => /attachment:([a-f0-9-]+)\/([a-f0-9-]+)/gi;

export function attachmentReferences(data) {
  const refs = new Map(), visited = new Set();
  function scan(value) {
    if (typeof value === 'string') {
      for (const match of value.matchAll(referencePattern())) {
        const ref = `${match[1]}/${match[2]}`.toLowerCase();
        refs.set(ref, { docId: match[1].toLowerCase(), attachmentId: match[2].toLowerCase() });
      }
    } else if (value && typeof value === 'object' && !visited.has(value)) {
      visited.add(value);
      Object.values(value).forEach(scan);
    }
  }
  scan(data);
  return refs;
}

export function replaceAttachmentReferences(text, replacements) {
  return text.replace(referencePattern(), (original, docId, blobId) =>
    replacements.get(`${docId}/${blobId}`.toLowerCase()) || original);
}

export function remapAttachmentData(data, replacements) {
  if (typeof data === 'string') return replaceAttachmentReferences(data, replacements);
  if (Array.isArray(data)) return data.map(value => remapAttachmentData(value, replacements));
  if (data && typeof data === 'object') return Object.fromEntries(Object.entries(data)
    .map(([key, value]) => [key, remapAttachmentData(value, replacements)]));
  return data;
}

export function safeArchiveFilename(name, fallback = 'attachment.bin') {
  const safe = String(name || '').normalize('NFC')
    .replace(/[\u0000-\u001f\u007f<>:"/\\|?*]/g, '_')
    .replace(/^[. ]+|[. ]+$/g, '').slice(0, 140);
  return /^(con|prn|aux|nul|com[1-9]|lpt[1-9])([.]|$)/i.test(safe) ? `_${safe}` : safe || fallback;
}

export function archiveLink(path) {
  return path.split('/').map(part => encodeURIComponent(part).replace(/[!'()*]/g,
    char => `%${char.charCodeAt(0).toString(16).toUpperCase()}`)).join('/');
}

async function digest(bytes) {
  return [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))]
    .map(b => b.toString(16).padStart(2, '0')).join('');
}

/** documents: [{id, data, listAll}], list/download are provided by the caller. */
export async function buildMapArchive({ blob, filename, documents = [], includeAttachments = true,
  warnings = [], list, download, onProgress, onActivity }) {
  if (blob.size > MAX_MAP_BYTES) throw new Error(t('vault.archive.mapTooLarge'));
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const issues = [...warnings];
  const refs = new Map();
  onActivity?.({ label: t('vault.archive.finding') });
  for (const document of includeAttachments ? documents : []) {
    const found = attachmentReferences(document.data);
    if (document.listAll) {
      try {
        for (const item of await list(document.id)) found.set(`${document.id}/${item.attachmentId}`.toLowerCase(),
          { docId: document.id, attachmentId: item.attachmentId });
      } catch (error) { issues.push({ name: document.name || document.id, error: t('vault.archive.listUnavailable', { error: error.message }) }); }
    }
    for (const [ref, source] of found) {
      if (!refs.has(ref)) refs.set(ref, { ...source, usedBy: new Set() });
      refs.get(ref).usedBy.add(document.id);
    }
  }
  let size = blob.size;
  let processed = 0;
  const attachments = [], paths = new Map();
  for (const [ref, source] of refs) {
    onProgress?.(processed, refs.size);
    try {
      if (size >= MAX_ARCHIVE_BYTES) throw new Error(t('vault.archive.sizeLimitReached'));
      const result = await download(source.docId, source.attachmentId, { onProgress: event =>
        onActivity?.({ ...event, label: t('vault.archive.fileOf', { label: event.label, current: processed + 1, total: refs.size }) }) });
      if (result.blob.size > MAX_ATTACHMENT_BYTES || size + result.blob.size > MAX_ARCHIVE_BYTES) {
        throw new Error(t('vault.archive.attachmentTooLarge'));
      }
      const bytes = new Uint8Array(await result.blob.arrayBuffer());
      onActivity?.({ label: t('vault.archive.checking'), current: processed, total: refs.size });
      const name = safeArchiveFilename(result.filename, `${source.attachmentId}.bin`);
      const path = `attachments/${String(attachments.length + 1).padStart(6, '0')}_${name}`;
      zip.file(path, bytes);
      size += bytes.length;
      attachments.push({ ref, path, filename: name, mimeType: result.mimeType || result.blob.type,
        size: bytes.length, sha256: await digest(bytes), usedBy: [...source.usedBy] });
      paths.set(ref, archiveLink(path));
    } catch (error) { issues.push({ name: ref, error: t('vault.archive.attachmentMissing', { error: error.message }) }); }
    processed++;
    onProgress?.(processed, refs.size);
  }
  onActivity?.({ label: t('vault.archive.preparing') });
  const mapFile = safeArchiveFilename(filename, 'map.json');
  if (mapFile === ARCHIVE_MANIFEST) throw new Error('Reserved archive filename');
  let bytes = new Uint8Array(await blob.arrayBuffer());
  // Markdown remains directly usable after unzip. JSON keeps stable source
  // references; the importer uses the manifest to re-encrypt and relink them.
  if (/\.md$/i.test(mapFile)) bytes = new TextEncoder().encode(
    replaceAttachmentReferences(new TextDecoder().decode(bytes), paths));
  if (bytes.length > MAX_MAP_BYTES) throw new Error(t('vault.archive.mapTooLarge'));
  zip.file(mapFile, bytes);
  const manifest = { format: 'orbital-map-archive', version: 1, exportedAt: new Date().toISOString(),
    mapFile, mapSha256: await digest(bytes), documentIds: documents.map(d => d.id),
    attachmentsIncluded: includeAttachments, attachments, warnings: issues, complete: issues.length === 0 };
  const manifestJson = JSON.stringify(manifest, null, 2);
  if (new TextEncoder().encode(manifestJson).length > 4 * 1024 * 1024 || attachments.length > 19997) {
    throw new Error(t('vault.archive.tooManyFiles'));
  }
  zip.file(ARCHIVE_MANIFEST, manifestJson);
  // STORE avoids zip expansion bombs in our own archives and needless work
  // recompressing images/audio. Imported third-party archives are bounded too.
  const output = await zip.generateAsync({ type: 'uint8array', compression: 'STORE' },
    metadata => onActivity?.({ label: t('vault.archive.building'), current: metadata.percent, total: 100 }));
  if (output.length > MAX_ARCHIVE_BYTES) throw new Error(t('vault.archive.exportTooLarge'));
  return { blob: new Blob([output], { type: 'application/zip' }), manifest, warnings: issues };
}

export async function readBoundedZipEntry(entry, maxBytes) {
  if (!entry || entry.dir) throw new Error(t('vault.archive.fileMissing'));
  return new Promise((resolve, reject) => {
    const chunks = [];
    let size = 0, stopped = false;
    const stream = entry.internalStream('uint8array');
    stream.on('data', chunk => {
      if (stopped) return;
      size += chunk.length;
      if (size > maxBytes) {
        stopped = true; stream.pause(); chunks.length = 0;
        reject(new Error(t('vault.archive.expands')));
      } else chunks.push(chunk);
    });
    stream.on('error', reject);
    stream.on('end', () => {
      if (stopped) return;
      const bytes = new Uint8Array(size);
      let offset = 0;
      for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
      resolve(bytes);
    });
    stream.resume();
  });
}

export async function readMapArchive(file, onActivity) {
  onActivity?.({ label: t('vault.archive.reading') });
  if (file.size > MAX_ARCHIVE_BYTES) throw new Error(t('vault.archive.importTooLarge'));
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const entries = Object.entries(zip.files);
  if (entries.length > 20000) throw new Error(t('vault.archive.tooManyEntries'));
  for (const [path, entry] of entries) {
    const original = entry.unsafeOriginalName || path;
    if (original !== path || /(^\/|\\|(^|\/)\.\.(\/|$)|^[a-z]:)/i.test(path)) {
      throw new Error(t('vault.archive.unsafePath'));
    }
  }
  if (!zip.file(ARCHIVE_MANIFEST)) return null; // Older Markdown/image ZIP.
  const manifest = JSON.parse(new TextDecoder().decode(await readBoundedZipEntry(zip.file(ARCHIVE_MANIFEST), 4 * 1024 * 1024)));
  if (manifest.format !== 'orbital-map-archive' || manifest.version !== 1 ||
      !Array.isArray(manifest.attachments) || !Array.isArray(manifest.documentIds)) throw new Error(t('vault.archive.unsupported'));
  const mapBytes = await readBoundedZipEntry(zip.file(manifest.mapFile), MAX_MAP_BYTES);
  onActivity?.({ label: t('vault.archive.checkingIntegrity') });
  if (await digest(mapBytes) !== manifest.mapSha256) throw new Error(t('vault.archive.mapIntegrity'));
  const attachments = new Map();
  let size = mapBytes.length;
  const paths = new Set([manifest.mapFile, ARCHIVE_MANIFEST]);
  // Validate every referenced file before creating or changing any map.
  for (const item of manifest.attachments) {
    onActivity?.({ label: t('vault.archive.unpacking'), current: attachments.size, total: manifest.attachments.length });
    if (typeof item.ref !== 'string' || !/^[a-f0-9-]+\/[a-f0-9-]+$/i.test(item.ref) ||
        typeof item.path !== 'string' || !item.path.startsWith('attachments/') || paths.has(item.path) ||
        attachments.has(item.ref.toLowerCase()) || !Array.isArray(item.usedBy) ||
        !Number.isSafeInteger(item.size) || item.size < 0 || item.size > MAX_ATTACHMENT_BYTES) throw new Error(t('vault.archive.invalidManifest'));
    const bytes = await readBoundedZipEntry(zip.file(item.path), Math.min(item.size, MAX_ARCHIVE_BYTES - size));
    size += bytes.length;
    if (bytes.length !== item.size || await digest(bytes) !== item.sha256) throw new Error(t('vault.archive.attachmentIntegrity'));
    paths.add(item.path);
    attachments.set(item.ref.toLowerCase(), { ...item, bytes });
  }
  return { manifest, mapBytes, attachments };
}
